import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import {
  PRIMARY_BLUE,
  PRIMARY_FONT,
  SECONDARY_FONT,
  LIGHT_FONT,
  ERROR_COLOR,
  ERROR_LIGHT,
  SECONDARY_GREY,
} from "src/constants/colors";

interface PlaceholderProps {
  isFocused: boolean;
  hasValue: boolean;
  showError: boolean;
  hasBeenFocused: boolean;
}

export const wiggle = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  45% {
    transform: translateX(5px);
  }
  65% {
    transform: translateX(-5px);
  }
  85% {
    transform: translateX(5px);
  }
`;

export const showErrorKeyframes = keyframes`
  0% {
    height: 0;
    margin: 0;
    opacity: 0;
  }
  20% {
    height: 0;
    margin: 0;
    opacity: 0;
  }
  70% {
    height: 10px;
    margin: -6px 0px 12px;
    opacity: 0.1;
  }
  75% {
    height: 10px;
    margin: -6px 0px 12px;
    opacity: 0.3;
  }
  100% {
    height: 10px;
    margin: -6px 0px 12px;
    opacity: 1;
  }
`;

export const placeholderFocusAnimation = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-6%);
  }
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  &.wiggle-animation {
    animation: ${wiggle} 0.3s ease-in-out;
  }
`;

export const InputElement = styled.input<{
  showError: boolean;
  hasValue: boolean;
}>`
  position: relative;
  z-index: 2;
  background-color: rgba(240, 241, 244, 1);
  border: 0;
  border-radius: 12px;
  box-sizing: border-box;
  color: ${PRIMARY_FONT};
  font-size: 16px;
  font-weight: 400;
  height: 60px;
  margin-bottom: 16px;
  padding: 20px;
  width: 100%;

  ${(props) =>
    props.showError
      ? css`
          background-color: rgba(250, 250, 251, 1);
          box-shadow: ${props.hasValue ? "0px 0px 0px 7px" : "0px 0px 0px 6px"}
            ${ERROR_LIGHT};
          caret-color: ${ERROR_COLOR};
          outline: ${props.hasValue ? "3px" : "2px"} solid ${ERROR_COLOR};
          transition:
            outline 0.3s ease,
            box-shadow 0.3s ease;
        `
      : css``}
  &:focus {
    background-color: rgba(250, 250, 251, 1);
    box-shadow: 0px 0px 0px 7px rgba(20, 83, 245, 0.12);
    caret-color: ${PRIMARY_BLUE};
    outline: 3px solid ${PRIMARY_BLUE};
    transition: box-shadow 0.1s ease;
  }
`;

export const ErrorMessage = styled.div`
  position: absolute;
  bottom: -17px;
  animation: ${showErrorKeyframes} 140ms ease;
  color: ${ERROR_COLOR};
  font-size: 12px;
  height: 10px;
  margin: -6px 0px 12px;
  padding-left: 20px;
  text-align: left;
  width: 100%;
`;

export const Placeholder = styled.div<PlaceholderProps>`
  position: absolute;
  display: none;
  z-index: 1;
  width: fit-content;
  background-color: transparent;
  font-size: 16px;
  color: ${SECONDARY_FONT};
  transition:
    top 0.3s ease,
    left 0.3s ease;

  ${({ isFocused, hasBeenFocused }) =>
    isFocused &&
    css`
      display: inline-flex;
      padding: 6px 8px 40px 8px;
      align-items: flex-start;
      gap: 10px;
      top: -23px;
      left: -3px;
      font-size: 12px;
      color: ${LIGHT_FONT};
      ${!hasBeenFocused &&
      `animation: ${placeholderFocusAnimation} 0.3s ease forwards`};
      border-radius: 6px 6px 0 0;
      background: ${PRIMARY_BLUE};
    `}

  ${({ isFocused, showError, hasValue }) =>
    !isFocused &&
    showError &&
    hasValue &&
    css`
      display: inline-flex;
      padding: 6px 8px 40px 8px;
      align-items: flex-start;
      gap: 10px;
      top: -23px;
      left: -3px;
      font-size: 12px;
      color: ${LIGHT_FONT};
      border-radius: 6px 6px 0 0;
      background: ${ERROR_COLOR};
    `}

  ${({ isFocused, showError, hasValue }) =>
    hasValue &&
    !isFocused &&
    !showError &&
    css`
      display: inline-flex;
      padding: 6px 8px;
      align-items: flex-start;
      gap: 10px;
      top: -10px;
      left: 16px;
      z-index: 3;
      font-size: 12px;
      color: ${SECONDARY_FONT};
      background-color: ${SECONDARY_GREY};
      border-radius: 6px;
    `}
`;
